/**
 * Canonical error display helpers.
 *
 * Maps `ApiError` codes (and raw values) to user-facing copy via i18n keys,
 * and formats operator-facing surfaces like support IDs. Every UI surface
 * that renders an `ApiError` should go through these helpers instead of
 * ad-hoc string checks.
 *
 * Rendering policy:
 *
 *   validation_failed       → i18n title + inline form field details
 *   unauthorized / forbidden → auth redirect or permission toast
 *   not_found               → empty/table state with clear message
 *   conflict / idempotency_conflict → toast with details
 *   rate_limited / quota_exceeded → toast with retry/usage hint
 *   internal_error / readiness_check_failed → toast + support ID
 *   network_error           → toast with connection hint
 */

import { type TFunction } from "i18next";
import {
	ApiError,
	type ApiErrorCode,
	isValidation,
	isAuth,
	isNotFound,
	isConflict,
	isRateLimited,
	isQuotaExceeded,
	isReadiness,
} from "./errors";

// ── i18n key mapping ────────────────────────────────────────────────────────

/** Map a canonical error code to its i18n title key. */
const CODE_TO_TITLE_KEY: Record<ApiErrorCode, string> = {
	validation_failed: "component.error.validationFailed",
	unauthorized: "component.error.unauthorized",
	forbidden: "component.error.forbidden",
	not_found: "component.error.notFound",
	conflict: "component.error.conflict",
	idempotency_conflict: "component.error.idempotencyConflict",
	quota_exceeded: "component.error.quotaExceeded",
	rate_limited: "component.error.rateLimited",
	readiness_check_failed: "component.error.readinessCheckFailed",
	internal_error: "component.error.internalError",
	network_error: "component.error.networkError",
};

// ── Public helpers ──────────────────────────────────────────────────────────

/**
 * Resolve the error code from any value — an `ApiError`, a raw envelope-like
 * object, or an unknown caught error.
 */
function resolveCode(error: unknown): ApiErrorCode | undefined {
	if (error instanceof ApiError) return error.code;
	if (error && typeof error === "object") {
		const candidate = (error as Record<string, unknown>).code;
		if (typeof candidate === "string") return candidate as ApiErrorCode;
	}
	return undefined;
}

/**
 * Resolve the request ID from any value.
 */
function resolveRequestId(error: unknown): string | undefined {
	if (error instanceof ApiError) return error.requestId;
	if (error && typeof error === "object") {
		const candidate = (error as Record<string, unknown>).request_id;
		if (typeof candidate === "string" && candidate.trim())
			return candidate.trim();
		const header = (error as Record<string, unknown>).requestId;
		if (typeof header === "string" && header.trim()) return header.trim();
	}
	return undefined;
}

/**
 * Get the user-facing title for an error.
 *
 * The **frontend owns** all user-facing copy for known canonical error codes.
 * When the backend sends a recognised code, the i18n translation is used
 * regardless of whether the backend also sent a `message`. This keeps
 * localization in one place and prevents the backend from silently
 * overriding user-visible text.
 *
 * Fallback order:
 *   1. Known canonical code → `t(CODE_TO_TITLE_KEY[code])`
 *   2. `ApiError` with a non-empty `message` but **no** known code → backend
 *      message (safe fallback for unexpected codes).
 *   3. Neither code nor message → `t("component.error.requestFailed")`.
 */
export function errorTitle(error: unknown, t: TFunction): string {
	const code = resolveCode(error);

	// For conflicts, prefer a specific backend message when present so operators
	// see "node is still referenced" instead of a generic stale-ETag toast.
	if (code === "conflict" && error instanceof ApiError) {
		const msg = (error.message || "").trim();
		if (msg && !/modified by someone else|stale/i.test(msg)) {
			const details = error.details as { referenced_by?: string[] } | undefined;
			if (details?.referenced_by?.length) {
				return `${msg} (${details.referenced_by.join(", ")})`;
			}
			return msg;
		}
	}

	// Frontend-owned i18n copy for every known canonical code.
	if (code && code in CODE_TO_TITLE_KEY) {
		return t(CODE_TO_TITLE_KEY[code]);
	}

	// Backend message is a safe fallback only for unknown / unexpected codes.
	if (error instanceof ApiError && error.message) {
		return error.message;
	}

	return t("component.error.requestFailed");
}

/**
 * Get the user-facing description for an error.
 *
 * Currently this is empty for most codes — the title carries the full copy.
 * Future per-field validation details would go here.
 */
export function errorDescription(
	_error: unknown,
	_t: TFunction,
): string | undefined {
	return undefined;
}

/**
 * Format the support ID line for operator/debug surfaces.
 *
 * Returns `undefined` when no request ID is available, so the caller
 * can conditionally render the collapsible section.
 */
export function formatSupportId(
	error: unknown,
	t: TFunction,
): string | undefined {
	const requestId = resolveRequestId(error);
	if (!requestId) return undefined;
	return t("component.error.supportId", { id: requestId });
}

/**
 * Check whether an error represents a not-found state that should
 * render as an empty table state rather than a toast.
 */
export function isNotFoundState(error: unknown): boolean {
	return isNotFound(error);
}

/**
 * Check whether an error should suppress the default toast (e.g. auth
 * errors handled by a redirect).
 */
export function isSuppressedToast(error: unknown): boolean {
	return isAuth(error);
}

/**
 * Check whether an error should show a retry prompt in the table.
 */
export function isRetryable(error: unknown): boolean {
	return isRateLimited(error) || isReadiness(error);
}

/**
 * Check whether an error should show a quota hint.
 */
export function isQuotaError(error: unknown): boolean {
	return isQuotaExceeded(error);
}

/**
 * Check whether an error should show as a conflict message.
 */
export function isConflictError(error: unknown): boolean {
	return isConflict(error);
}

/**
 * Check whether an error is a validation error.
 */
export function isValidationError(error: unknown): boolean {
	return isValidation(error);
}
